/**
 * Per-metric reactive bundle composing the registry, formatter,
 * state-machine, and rAF smoothing into one `{ raw, smoothed, formatted,
 * unit, phase, state, isStarted, isFinished, isActiveOrStarted, hasData }`
 * object.
 *
 * The plural `useMetricResults()` is the canonical entry — it returns a
 * `Record<MetricId, UseMetricResult>` and uses glass-ui's
 * `useAnimatedNumberMap` (v0.8.4) to fan out the 4 smoothers behind one
 * factory call instead of 4 separate `useAnimatedNumber` invocations.
 * The smoothing options apply uniformly across the metric set.
 *
 * `useMetricResult(id)` (singular) survives as a thin shim over the
 * plural for any caller that needs a unitary bundle. Most consumers
 * (MetricPillCluster, SpeedtestResults) read the plural directly so the
 * 4-up smoothing fan-out concentrates in one composable owner.
 *
 * Internals: `useMetricFormatter` for the post-W3 jitter-safe `format`,
 * `useMetricStateMachine` for the lifecycle predicates, the speedtest
 * store's `resultFor(id)` for the raw `UnitInfo.value`, and
 * `useAnimatedNumberMap` for the DOM smoothing layer.
 *
 * Call once at setup; never inside a v-for. The plural form lifts the
 * v-for-incompatibility constraint to the call site (the surrounding
 * setup scope owns the rAF subscriptions).
 */

import { computed, type ComputedRef } from "vue";

import {
    DAMPING,
    SNAP_THRESHOLD,
    useAnimatedNumberMap,
} from "@mkbabb/glass-ui/motion";

import {
    METRIC_IDS,
    phaseFor,
    type MetricId,
    type PhaseId,
} from "@src/types/speedtest.ts";
import { TestState } from "@features/speedtest/state/index.ts";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";

import { useMetricFormatter } from "./useMetricFormatter.ts";
import { useMetricStateMachine } from "./useMetricStateMachine.ts";

export interface UseMetricResult {
    /** Raw numeric value from the store, or null when no data. */
    raw: ComputedRef<number | null>;
    /** rAF-smoothed value. Null when the source has no sample yet. */
    smoothed: ComputedRef<number | null>;
    /** Formatted display amount (string), driven by `smoothed`. */
    formatted: ComputedRef<string>;
    /** Display unit; falls back to the metric's defaultUnit when null. */
    unit: ComputedRef<string>;
    /** Driving engine phase. Jitter rides ping. */
    phase: PhaseId;
    /** Per-metric lifecycle state. */
    state: ComputedRef<TestState>;
    /** True when state is `started`. */
    isStarted: ComputedRef<boolean>;
    /** True when state is `finished`. */
    isFinished: ComputedRef<boolean>;
    /** True when state is `started` OR `active`. */
    isActiveOrStarted: ComputedRef<boolean>;
    /** True when the formatter has produced a non-empty amount. */
    hasData: ComputedRef<boolean>;
}

const PILL_OPTS = {
    damping: DAMPING.domPill,
    snapThreshold: SNAP_THRESHOLD.dom,
} as const;

/**
 * Plural form — returns a `Record<MetricId, UseMetricResult>` with one
 * shared `useAnimatedNumberMap` driving the smoothing for all 4 metrics.
 *
 * Per-metric raw refs are read from the store via `resultFor(id)`; the
 * map of getters is passed to `useAnimatedNumberMap`, which fans out the
 * smoothing in a single composable owner. `null` propagation is preserved
 * (the smoother does not freeze on the last sample when raw goes null).
 */
export function useMetricResults(): Record<MetricId, UseMetricResult> {
    const speedtest = useSpeedtestStore();
    const formatter = useMetricFormatter();
    const stateMachine = useMetricStateMachine();

    // Build per-metric raw getters once. The getter closure reads from the
    // speedtest store, so reactivity flows through the store's reactivity.
    const rawRefs = METRIC_IDS.reduce(
        (acc, id) => {
            acc[id] = computed<number | null>(
                () => speedtest.resultFor(id).rawAmount ?? null,
            );
            return acc;
        },
        {} as Record<MetricId, ComputedRef<number | null>>,
    );

    // The `as never` cast at the source-callback boundary is load-bearing.
    // useAnimatedNumberMap's signature reads `ComputedRef<number | null>`
    // from glass-ui's bundled vue typedefs; speedtest's `computed()` here
    // produces a `ComputedRef<number | null>` from speedtest's bundled
    // vue typedefs. Structurally identical; vue-tsc sees them as distinct
    // because the [ComputedRefSymbol] / [RefSymbol] private slots come
    // from two type-only modules. The runtime values are the same Vue
    // ref proxy. Cross-package vue-dedup is the structural fix.
    //
    // Deferred to AF+1 (decision AF2-D3): the cross-package vue-dedup is
    // engineering-infrastructure of a different risk profile than this
    // design tranche. Until then, this cast is the surface-level
    // reconciliation.
    const smoothedRefs = useAnimatedNumberMap({
        keys: METRIC_IDS,
        source: ((id: MetricId) => rawRefs[id]) as never,
        ...PILL_OPTS,
    });

    return METRIC_IDS.reduce(
        (acc, id) => {
            const raw = rawRefs[id];
            // Cross-package vue type-only mismatch — see the `as never`
            // narrowing at the source-callback boundary above. The
            // runtime shape is identical (same Vue ref proxy); the
            // speedtest-side `computed()` re-wrap reads `.value` through
            // a speedtest-domain ComputedRef so no cross-package cast
            // is needed in the return interface.
            const glassSmoothed = smoothedRefs[id];
            const smoothed = computed<number | null>(() => glassSmoothed.value);

            const formattedPair = computed(() =>
                formatter.format(id, smoothed.value),
            );
            const formatted = computed(() => formattedPair.value.amount);
            const unit = computed(() => formattedPair.value.unit);

            acc[id] = {
                raw,
                smoothed,
                formatted,
                unit,
                phase: phaseFor(id),
                state: stateMachine.state(id),
                isStarted: stateMachine.isStarted(id),
                isFinished: stateMachine.isFinished(id),
                isActiveOrStarted: stateMachine.isActiveOrStarted(id),
                hasData: computed(() => formatted.value !== ""),
            };
            return acc;
        },
        {} as Record<MetricId, UseMetricResult>,
    );
}

/**
 * Singular form — returns a single metric's bundle. Internally calls
 * `useMetricResults()` and indexes; the per-call cost is one map
 * construction, paid once at setup. Prefer `useMetricResults()` directly
 * when the consumer reads the full set.
 */
export function useMetricResult(id: MetricId): UseMetricResult {
    return useMetricResults()[id];
}
